import * as dateMath from 'date-arithmetic';
import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { assertNever, rng } from '../../amorg-common';
import { SafetyInspectionActivityListing } from '../../amorg-common/DTOs/SafetyInspectionActivityListing';
import { createActivity, getSafetyInspectionActivities } from '../../api/activityClientApi';
import { Button } from '../../designSystem/button/Button';
import { InfoChip } from '../../designSystem/Chip';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../designSystem/PageHeader';
import { PageSection } from '../../designSystem/PageSection';
import { PageSectionTable, PageSectionRow, PageSectionCell, PageSectionTableProps } from '../../designSystem/PageSectionTable';
import { P, Small } from '../../designSystem/typography/Body';
import { txt } from '../../txt';
import { clientAccessHelper } from '../../utils/clientAccessHelper';
import { toDateString } from '../../utils/date';
import { ProtectedPageContext } from '../_contexts/ProtectedPageContext';
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';
import { UserPageWrapper } from '../_pageWrappers/UserPageWrapper';
import { IconWithText } from '../../designSystem/compositeComponents/compositeIcons/IconWithText';

export default function SafetyInspectionListingPage(): JSX.Element 
{
	return (
		<UserPageWrapper selectedMenuItemKey={'safetyinspection'} breadCrumbs={[{ text: 'Sikkerhedsrunderinger', url: '' }]}>
            <SafetyInspectionListingPageContent />
		</UserPageWrapper>
	);
}

function SafetyInspectionListingPageContent(): JSX.Element
{
    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewAssignedSafetyInspections']);
    const canCreate = clientAccessHelper.hasAllPermissions(activeUser, ['EditSafetyInspection', 'EditActivity']);

    if (!hasAccess)
        return <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>;

    return <SafetyInspectionListingView canCreate={canCreate} />;
}

type SafetyInspectionListingViewState = {
	loading:boolean,
	activities:SafetyInspectionActivityListing[],
    redirectId:string
}

function SafetyInspectionListingView(props:{canCreate:boolean}): JSX.Element
{
	const [state, setState] = React.useState<SafetyInspectionListingViewState>({
        loading:true,
        activities:[],
        redirectId:''
    });

    React.useEffect(()=>{
        const getData = async () => {
            const activities = await getSafetyInspectionActivities();
            activities.sort((a,b)=> a.start.getTime() - b.start.getTime());
            // Get activities convert start and end from strings (JSON transport) to dates
            setState({activities, loading:false, redirectId:''});
        }
        getData();
    },[])

    function handleClickNavigate(activityId:string)
    {
        setState({...state, redirectId:activityId});
    }

    async function handleClickNew()
    {
        const now = new Date();

        const activityId = rng.getRandomId();
        const startDate = dateMath.add(new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours(), 0, 0, 0), 1, 'day');
        const endDate = dateMath.add(startDate, 1, 'day');

        setState({...state, loading:true});

        const result = await createActivity(
            activityId,
            'SafetyInspection',
            txt.NewActivityType_SafetyInspection,
            '',
            startDate,
            endDate,
            ''
        );

        if(result.isSuccess){
            setState({...state, redirectId:activityId});
        }
    }

    const current = state.activities?.filter(a => a.end >= new Date()) || [];
    const past = state.activities?.filter(a => a.end < new Date()) || [];

    return (
        <>
            <PageHeader
                headerText="Sikkerhedsrunderinger"
                headerIcon="verified_user"
                buttons={props.canCreate &&
                    <Button variant="filled" size="32px" text="Opret ny" icon="actions_plus" onClick={handleClickNew} />
                }
            />
            <SingleColumnLayout>
            <>
                {state.redirectId !== '' &&
                    <Navigate to={`/safetyinspections/${state.redirectId}`} />
                }

                <LoadingPageOverlay loading={state.loading} />
                
                <PageSection
                    header={{
                        title: 'Aktuelle Sikkerhedsrunderinger',
                        icon:'shield',
                    }}
                    headerStyleVariant='minimal'
                    contentStyleVariant='minimal'
                    contentPadding='16px 0'
                >
                    <PageSectionTable
                        headerColumns={getHeaders()}
                        noContentText="Ingen aktuelle sikkerhedsrunderinger"
                        showAllLimit={5}>

                        {current.map(activity => getDataRow(activity, handleClickNavigate))}

                    </PageSectionTable>
                
                </PageSection>

                {past.length > 0 &&
                    <>
                    <PageSection
                        header={{
                            title: 'Tidligere Sikkerhedsrunderinger',
                            icon:'verified_user',
                        }}
                        headerStyleVariant='minimal'
                        contentStyleVariant='minimal'
                        contentPadding='16px 0'>
                            
                        <PageSectionTable
                            headerColumns={getHeaders()}
                            noContentText="Ingen tidligere sikkerhedsrunderinger"
                            showAllLimit={5}>
                        
                            {past.map(activity => getDataRow(activity, handleClickNavigate))}

                        </PageSectionTable>
                    </PageSection>
                    </>
                }
            </>
        </SingleColumnLayout>
        </>
	);
}

function getHeaders(): PageSectionTableProps['headerColumns'] {
    return [
        {text:'Titel'},
        {text:'Status'},
        {text:''},
        {text:'Periode'},
        {text:'Lokation'}
    ];
}

function getDataRow(activity: SafetyInspectionActivityListing, onNavigate: (id: string) => void): React.ReactElement {
    return (
        <PageSectionRow key={activity.id} onClick={() => onNavigate(activity.id)}>
            <PageSectionCell>{activity.title}</PageSectionCell>
            <PageSectionCell><Status1Content activity={activity} /></PageSectionCell>
            <PageSectionCell><Status2Content activity={activity} /></PageSectionCell>
            <PageSectionCell><PeriodContent activity={activity} /></PageSectionCell>
            <PageSectionCell><LocationContent activity={activity} /></PageSectionCell>
        </PageSectionRow>
    );
}

const activityStatus = (activity:SafetyInspectionActivityListing): 'planned' | 'open' | 'closed' => {
    const now = new Date();
    if(activity.start > now)
        return 'planned';
    if(activity.end < now)
        return 'closed';
    return 'open';
}

function Status1Content(props:{activity:SafetyInspectionActivityListing}):JSX.Element
{
    const status = activityStatus(props.activity);

    let primary = <></>

    if(status === 'planned')
        primary = <IconWithText icon="clock" variant='active'>Planlagt</IconWithText>
    else if(status === 'closed')
        primary = <IconWithText icon="status_ok" variant='success'>Afsluttet</IconWithText>;
    else if(status === 'open')
        primary = <IconWithText icon="interface_glyph_hourglass" variant='warning'>I gang</IconWithText>;
    else
        assertNever(status);

    return primary;
}

function Status2Content(props:{activity:SafetyInspectionActivityListing}):JSX.Element
{
    const inspectionPoints = props.activity.locations.flatMap(l => l.facilities.flatMap(f => f.inspections));
    const total = inspectionPoints.reduce((acc, p) => acc + p.failed + p.passed + p.pending, 0);
    const passed = inspectionPoints.reduce((acc, p) => acc + p.passed, 0);

    const secondary =   <Flex flexDirection="column" alignItems="flex-start" gap={4}>
                            <Flex alignItems="center" gap={4}>
                                <IconWithText icon="fact_check" variant='active' iconSize="x-small" fontSize="14px">{total + " kontrolpunkt(er)"}</IconWithText>
                            </Flex>
                            <Flex alignItems="center" gap={4}>
                                <IconWithText icon="status_ok" variant="success" iconSize="x-small" fontSize="14px">{passed + " godkendt"}</IconWithText>
                            </Flex>
                        </Flex>;

    return secondary;
}

function PeriodContent(props:{activity:SafetyInspectionActivityListing}):JSX.Element
{
    return (
        <Flex flexWrap="wrap">
            <div style={{whiteSpace: 'nowrap'}}>{toDateString(props.activity.start)}  - </div>
            <div style={{whiteSpace: 'nowrap'}}>{toDateString(props.activity.end)}</div>
        </Flex>
    );
}

function LocationContent(props:{activity:SafetyInspectionActivityListing}):JSX.Element
{
    return (
        <Flex flexWrap="wrap" gap={4}>
            {props.activity.locations.map(l =>
                <InfoChip key={l.id}>{l.name}</InfoChip>
            )}

            {props.activity.locations.length === 0 &&
                <Small><i>Ingen lokationer tilføjet</i></Small>
            }
        </Flex>
    );
}